/**
 * The front door as amended (work order M6, amendment 1; Station 2b): the screens that
 * walk Chapter 1 from the surface.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * - CreateProjectDialog: the project AND its already-awarded main contract in one act,
 *   the contractor chosen from the register or registered inline. One POST /api/projects.
 * - ApprovalDialog: the approver's overlay. The vendor step appears when the server names
 *   the gap disciplines (409 project.vendor_approval_required); the self-approval refusal
 *   reads as the rule it is; personnel may be assigned in the same act.
 * - AssignDialog: one operator person, one role, POST /api/role-assignments.
 * - ProjectHistoryBlock: the plain-language provenance read.
 *
 * Every refusal renders inside its dialog, and a dialog closes only after its submission
 * resolves (UX_SPEC section 5). Rule codes stay in logs; the words on screen are plain.
 */

import { LoaderCircle, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { attachToTarget } from "../api/attachments";
import { ApiError } from "../api/client";
import {
  approveProject,
  assignPortalUserContracts,
  createProject,
  createRoleAssignment,
  getApprovalOutlook,
  getProject,
  getProjectHistory,
  getTaxonomies,
  listContracts,
  listParties,
  listProjects,
  revokeRoleAssignment,
  type ApprovalOutlook,
  type AssignPayload,
  type ProjectApproved,
  type ProjectCreated,
  type ProjectDetail,
  type ProjectSummary,
  type TaxonomyToken,
  type TeamMember,
} from "../api/portfolio";
import { strings } from "../brand/strings";
import { PICKER_CLASS, PickerOption, pickerWidth } from "./PickerOption";
import { ActionDialog } from "@/components/ui/action-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogFooter } from "@/components/ui/dialog";
import { Field, FieldError, FieldHelper, FormSection, Refusal } from "@/components/ui/field";
import { FilePicker } from "@/components/ui/file-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { COUNTRIES, CURRENCIES, countryCode, currencyCode } from "../lib/countries";
import { fieldErrorsFor, refusalText } from "../lib/serverErrors";
import { Facts, day } from "../screens/parts";
import { PartyAccountPicker } from "./PortalAccounts";
import { useQuery } from "../screens/useQuery";

const copy = strings.frontDoor;

/* ---------------------------------------------------------------------- */
/* The project status chip: the three amendment-1 states in plain words.  */
/* ---------------------------------------------------------------------- */

const STATUS_TONE: Record<string, "attention" | "success"> = {
  PENDING_APPROVAL: "attention",
  PENDING_ASSIGNMENT: "attention",
  REGISTERED: "success",
};

/** The project status in plain words; the chip and the register's filter both read it. */
export function projectStatusLabel(status: string | null): string {
  if (status === null) {
    return "";
  }
  return copy.status[status] ?? status.toLowerCase().replace(/_/g, " ");
}

export function ProjectStatusChip({ status }: { status: string | null }): React.JSX.Element | null {
  if (status === null) {
    return null;
  }
  const tone = STATUS_TONE[status];
  return (
    <span
      className={`q-chip${tone === undefined ? "" : ` q-chip--${tone}`}`}
      data-testid="project-status-chip"
      data-status={status}
    >
      {projectStatusLabel(status)}
    </span>
  );
}

/* ---------------------------------------------------------------------- */
/* Small form parts, on the shadcn/ui primitives (foundation pilot).      */
/* ---------------------------------------------------------------------- */

function TextField({
  id,
  label,
  value,
  onChange,
  error = null,
  type = "text",
  multiline = false,
  className,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly error?: string | null;
  readonly type?: "text" | "date";
  readonly multiline?: boolean;
  readonly className?: string;
}): React.JSX.Element {
  const errorId = `${id}-error`;
  const shared = {
    id,
    "data-testid": id,
    value,
    "aria-invalid": error !== null,
    "aria-describedby": error !== null ? errorId : undefined,
  };
  return (
    <Field className={className}>
      <Label htmlFor={id}>{label}</Label>
      {multiline ? (
        <Textarea
          {...shared}
          rows={3}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      ) : (
        <Input
          {...shared}
          type={type}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      )}
      <FieldError id={errorId} message={error} />
    </Field>
  );
}

function CheckList({
  tokens,
  selected,
  onToggle,
  testId,
  label,
}: {
  readonly tokens: readonly TaxonomyToken[];
  readonly selected: readonly string[];
  readonly onToggle: (id: string) => void;
  readonly testId: string;
  readonly label: string;
}): React.JSX.Element {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-sm tracking-[0.04em] text-muted-foreground">{label}</legend>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {tokens
          .flatMap((one) => (one.id === undefined ? [] : [{ ...one, id: one.id }]))
          .map((one) => (
            <Label
              key={one.id}
              className="min-h-11 cursor-pointer text-base tracking-normal text-foreground"
            >
              <Checkbox
                data-testid={`${testId}-${one.token}`}
                checked={selected.includes(one.id)}
                onCheckedChange={() => {
                  onToggle(one.id);
                }}
              />
              {one.label}
            </Label>
          ))}
      </div>
    </fieldset>
  );
}

function toggled(list: readonly string[], id: string): string[] {
  return list.includes(id) ? list.filter((one) => one !== id) : [...list, id];
}

/** A labelled native select, the form control of choice on a phone (see NativeSelect). */
function SelectField({
  id,
  label,
  value,
  onChange,
  children,
  invalid = false,
  className,
  columns = false,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly children: React.ReactNode;
  readonly invalid?: boolean;
  readonly className?: string;
  /** Wave Experience A: a two-column picker (identifier and name) keeps its columns. */
  readonly columns?: boolean;
}): React.JSX.Element {
  return (
    <Field className={className}>
      <Label htmlFor={id}>{label}</Label>
      <NativeSelect
        id={id}
        data-testid={id}
        className={columns ? PICKER_CLASS : undefined}
        value={value}
        aria-invalid={invalid}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {children}
      </NativeSelect>
    </Field>
  );
}

/**
 * The submit row: the blocking reason beside the buttons, never hidden in a tooltip. It
 * sticks to the bottom of a long dialog, so the act and its reason never scroll away.
 */
function SubmitRow({
  reason,
  children,
}: {
  readonly reason: React.ReactNode;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <DialogFooter className="sticky -bottom-(--q-s1) z-10 -mx-(--q-s1) -mb-(--q-s1) justify-between bg-background px-(--q-s1) pb-(--q-s1)">
      <div className="min-w-0 flex-1 basis-[14rem] text-sm text-muted-foreground">{reason}</div>
      <div className="flex flex-wrap justify-end gap-3">{children}</div>
    </DialogFooter>
  );
}

/* ---------------------------------------------------------------------- */
/* Create project: the project, its awarded contract, its contractor.     */
/* ---------------------------------------------------------------------- */

type ContractorMode = "existing" | "new";

export interface CreateProjectDraft {
  name: string;
  number: string;
  costCentre: string;
  description: string;
  parentId: string;
  startDate: string;
  endDate: string;
  disciplines: string[];
  stages: string[];
  contractTitle: string;
  contractScope: string;
  contractDisciplines: string[];
  contractStart: string;
  contractEnd: string;
  contractValue: string;
  contractCurrency: string;
  contractorMode: ContractorMode;
  contractorPartyId: string;
  /** The connected account to assign to the new contract: a person id, or none. */
  portalPersonId: string;
  portalPersonName: string;
  newName: string;
  newTypeId: string;
  newRegistration: string;
  newCountry: string;
  newContact: string;
}

export const EMPTY_DRAFT: CreateProjectDraft = {
  name: "",
  number: "",
  costCentre: "",
  description: "",
  parentId: "",
  startDate: "",
  endDate: "",
  disciplines: [],
  stages: [],
  contractTitle: "",
  contractScope: "",
  contractDisciplines: [],
  contractStart: "",
  contractEnd: "",
  contractValue: "",
  contractCurrency: "",
  contractorMode: "existing",
  contractorPartyId: "",
  portalPersonId: "",
  portalPersonName: "",
  newName: "",
  newTypeId: "",
  newRegistration: "",
  newCountry: "",
  newContact: "",
};

/** The draft key: a half-finished registration survives a reload or an expired session. */
export const CREATE_PROJECT_DRAFT_KEY = "qualify.draft.create-project";

function readDraft(): CreateProjectDraft | null {
  try {
    const raw = localStorage.getItem(CREATE_PROJECT_DRAFT_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<CreateProjectDraft>;
    const draft = { ...EMPTY_DRAFT, ...parsed };
    // A draft saved while country and currency were free text reads back as codes: a
    // typed "Poland" becomes PL, anything unrecognised becomes not set (wave W1.5).
    return {
      ...draft,
      newCountry: countryCode(draft.newCountry),
      contractCurrency: currencyCode(draft.contractCurrency),
    };
  } catch {
    return null;
  }
}

type DraftErrors = Partial<Record<keyof CreateProjectDraft, string>>;

/** What the server accepts: up to 16 whole digits and at most two decimals. */
const NUMBER = /^\d{1,16}(\.\d{1,2})?$/;

/**
 * The server's field paths onto this form's own keys (wave W1.5, finding 2): every
 * place the server refuses is marked on the control that holds it.
 */
const SERVER_FIELDS: Readonly<Record<string, keyof CreateProjectDraft>> = {
  name: "name",
  project_number: "number",
  description: "description",
  cost_centre: "costCentre",
  parent_project_id: "parentId",
  start_date: "startDate",
  end_date: "endDate",
  discipline_ids: "disciplines",
  stage_ids: "stages",
  "contract.title": "contractTitle",
  "contract.scope_description": "contractScope",
  "contract.discipline_ids": "contractDisciplines",
  "contract.start_date": "contractStart",
  "contract.end_date": "contractEnd",
  "contract.value_amount": "contractValue",
  "contract.value_currency": "contractCurrency",
  "contract.contractor_party_id": "contractorPartyId",
  "contract.new_contractor.name": "newName",
  "contract.new_contractor.party_type_id": "newTypeId",
  "contract.new_contractor.registration_no": "newRegistration",
  "contract.new_contractor.country": "newCountry",
  "contract.new_contractor.contact_note": "newContact",
};

export function validateDraft(draft: CreateProjectDraft, typeAvailable: boolean): DraftErrors {
  const errors: DraftErrors = {};
  if (draft.name.trim() === "") {
    errors.name = copy.errorName;
  }
  if (draft.number.trim() === "") {
    errors.number = copy.errorNumber;
  }
  if (draft.startDate !== "" && draft.endDate !== "" && draft.endDate < draft.startDate) {
    errors.endDate = copy.errorDates;
  }
  if (draft.contractTitle.trim() === "") {
    errors.contractTitle = copy.errorContractTitle;
  }
  if (
    draft.contractStart !== "" &&
    draft.contractEnd !== "" &&
    draft.contractEnd < draft.contractStart
  ) {
    errors.contractEnd = copy.errorContractDates;
  }
  if (draft.contractValue.trim() !== "" && !NUMBER.test(draft.contractValue.trim())) {
    errors.contractValue = copy.errorValue;
  }
  if (draft.contractorMode === "existing" && draft.contractorPartyId === "") {
    errors.contractorPartyId = copy.errorContractor;
  }
  if (draft.contractorMode === "new") {
    if (draft.newName.trim() === "") {
      errors.newName = copy.errorNewContractorName;
    }
    if (typeAvailable && draft.newTypeId === "") {
      errors.newTypeId = copy.errorNewContractorType;
    }
  }
  return errors;
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** What the parent toasts once the registration lands. */
export interface CreateProjectOutcome {
  readonly created: ProjectCreated;
  readonly contractorName: string;
  readonly attachFailures: number;
  /** Set only when a chosen portal account could not be assigned: its name. */
  readonly accountAssignFailed?: string;
}

export function CreateProjectDialog({
  token,
  projects,
  mayRegisterContractor = true,
  onClose,
  onCreated,
}: {
  readonly token: string;
  readonly projects: readonly ProjectSummary[];
  /**
   * Whether this account holds PARTY CREATE (wave W1.5): without it the "register a new
   * contractor" mode is shown disabled with its reason, never offered to fail at submit.
   */
  readonly mayRegisterContractor?: boolean;
  readonly onClose: () => void;
  readonly onCreated: (outcome: CreateProjectOutcome) => void;
}): React.JSX.Element {
  const taxonomies = useQuery((signal) => getTaxonomies(token, signal), [token]);
  const parties = useQuery((signal) => listParties(token, signal), [token]);
  const [restored] = useState<CreateProjectDraft | null>(readDraft);
  const [draft, setDraft] = useState<CreateProjectDraft>(restored ?? EMPTY_DRAFT);
  const [files, setFiles] = useState<File[]>([]);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // The server's own per-field refusals, each cleared as its field is edited.
  const [serverErrors, setServerErrors] = useState<DraftErrors>({});

  // Every keystroke lands in the draft, so a reload or a lapsed session loses nothing.
  useEffect(() => {
    localStorage.setItem(CREATE_PROJECT_DRAFT_KEY, JSON.stringify(draft));
  }, [draft]);

  const set = <K extends keyof CreateProjectDraft>(key: K, value: CreateProjectDraft[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    setServerErrors((current) => {
      if (current[key] === undefined) {
        return current;
      }
      return Object.fromEntries(Object.entries(current).filter(([name]) => name !== key));
    });
  };

  const taxonomy = taxonomies.kind === "ready" ? taxonomies.data : null;
  const disciplineTokens = taxonomy?.disciplines ?? [];
  const stageTokens = taxonomy?.stages ?? [];
  // The new contractor's type: counterparty types the server sends with their row id.
  const contractorTypes = (taxonomy?.partyTypes ?? []).filter(
    (one) => one.id !== undefined && one.isInternal !== true,
  );
  const typeAvailable = contractorTypes.length > 0;
  const internalCodes = new Set(
    (taxonomy?.partyTypes ?? []).filter((one) => one.isInternal === true).map((one) => one.token),
  );
  const contractorOptions = (parties.kind === "ready" ? parties.data : []).filter(
    (one) => !internalCodes.has(one.partyTypeCode),
  );
  const contractScopeTokens = disciplineTokens.filter(
    (one) => one.id !== undefined && draft.disciplines.includes(one.id),
  );

  // A single counterparty type is chosen for the operator, not left as a trap.
  const onlyType = contractorTypes.length === 1 ? (contractorTypes[0]?.id ?? "") : "";
  const typeId = draft.newTypeId === "" ? onlyType : draft.newTypeId;

  const errors = validateDraft({ ...draft, newTypeId: typeId }, typeAvailable);
  const shown = (key: keyof CreateProjectDraft): string | null =>
    (tried ? errors[key] : undefined) ?? serverErrors[key] ?? null;
  const newModeBlocked =
    draft.contractorMode === "new" && (!typeAvailable || !mayRegisterContractor);

  const contractorName =
    draft.contractorMode === "new"
      ? draft.newName.trim()
      : (contractorOptions.find((one) => one.id === draft.contractorPartyId)?.name ?? "");

  const submit = async (): Promise<void> => {
    setTried(true);
    setRefusal(null);
    setServerErrors({});
    if (Object.keys(errors).length > 0 || newModeBlocked || busy) {
      return;
    }
    setBusy(true);
    try {
      const created = await createProject(token, {
        name: draft.name.trim(),
        projectNumber: draft.number.trim(),
        description: blankToNull(draft.description),
        costCentre: blankToNull(draft.costCentre),
        parentProjectId: draft.parentId === "" ? null : draft.parentId,
        disciplineIds: draft.disciplines,
        stageIds: draft.stages,
        startDate: blankToNull(draft.startDate),
        endDate: blankToNull(draft.endDate),
        contract: {
          title: draft.contractTitle.trim(),
          contractorPartyId: draft.contractorMode === "existing" ? draft.contractorPartyId : null,
          newContractor:
            draft.contractorMode === "new"
              ? {
                  name: draft.newName.trim(),
                  partyTypeId: typeId,
                  registrationNo: blankToNull(draft.newRegistration),
                  country: blankToNull(draft.newCountry),
                  contactNote: blankToNull(draft.newContact),
                }
              : null,
          scopeDescription: blankToNull(draft.contractScope),
          disciplineIds: draft.contractDisciplines.filter((one) => draft.disciplines.includes(one)),
          startDate: blankToNull(draft.contractStart),
          endDate: blankToNull(draft.contractEnd),
          valueAmount: blankToNull(draft.contractValue),
          valueCurrency: blankToNull(draft.contractCurrency),
        },
      });
      let attachFailures = 0;
      for (const file of files) {
        try {
          await attachToTarget(token, file, {
            entityType: "PROJECT",
            entityId: created.id,
            purpose: "GENERAL",
          });
        } catch {
          attachFailures += 1;
        }
      }
      // The chosen connected account is assigned to the contract the project just made;
      // a refusal here never undoes the registration, it is named for the parent.
      let accountAssignFailed: string | undefined;
      if (draft.contractorMode === "existing" && draft.portalPersonId !== "") {
        try {
          await assignPortalUserContracts(token, draft.contractorPartyId, draft.portalPersonId, {
            projectId: created.id,
          });
        } catch {
          accountAssignFailed =
            draft.portalPersonName === ""
              ? strings.portalAccounts.pickFallbackName
              : draft.portalPersonName;
        }
      }
      localStorage.removeItem(CREATE_PROJECT_DRAFT_KEY);
      onCreated({
        created,
        contractorName,
        attachFailures,
        ...(accountAssignFailed === undefined ? {} : { accountAssignFailed }),
      });
    } catch (error: unknown) {
      if (error instanceof ApiError && error.kind === "absence") {
        setRefusal(copy.refusedNoPermission);
      } else {
        // Every place the server refused is marked on its control, and the summary
        // names each in plain words (wave W1.5, finding 2).
        setServerErrors(fieldErrorsFor(error, SERVER_FIELDS));
        setRefusal(refusalText(error, copy.fieldNames));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ActionDialog open wide title={strings.portfolio.createProjectHeading} onClose={onClose}>
      {restored !== null && (
        <p
          className="rounded-md border border-sig-edge bg-sig-wash px-3 py-2 text-sm"
          data-testid="draft-restored"
        >
          {copy.draftRestored}
        </p>
      )}

      <FormSection>{copy.projectSection}</FormSection>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
        <TextField
          id="project-create-name"
          label={strings.portfolio.createNameLabel}
          value={draft.name}
          onChange={(value) => {
            set("name", value);
          }}
          error={shown("name")}
        />
        <TextField
          id="project-create-number"
          label={strings.portfolio.createNumberLabel}
          value={draft.number}
          onChange={(value) => {
            set("number", value);
          }}
          error={shown("number")}
        />
        <TextField
          id="project-create-cost-centre"
          label={copy.costCentreLabel}
          value={draft.costCentre}
          onChange={(value) => {
            set("costCentre", value);
          }}
          error={shown("costCentre")}
        />
        <Field>
          <SelectField
            id="project-create-parent"
            columns
            label={strings.portfolio.createParentLabel}
            value={draft.parentId}
            invalid={shown("parentId") !== null}
            onChange={(value) => {
              set("parentId", value);
            }}
          >
            <option value="">None</option>
            {projects.map((one) => (
              <PickerOption
                key={one.id}
                value={one.id}
                identifier={one.humanId}
                name={one.name}
                width={pickerWidth(projects.map((p) => p.humanId))}
              />
            ))}
          </SelectField>
          <FieldError id="project-create-parent-error" message={shown("parentId")} />
        </Field>
        <TextField
          id="project-create-start"
          type="date"
          label={strings.portfolio.createStartLabel}
          value={draft.startDate}
          onChange={(value) => {
            set("startDate", value);
          }}
          error={shown("startDate")}
        />
        <TextField
          id="project-create-end"
          type="date"
          label={strings.portfolio.createEndLabel}
          value={draft.endDate}
          onChange={(value) => {
            set("endDate", value);
          }}
          error={shown("endDate")}
        />
      </div>
      <TextField
        id="project-create-description"
        label={copy.descriptionLabel}
        value={draft.description}
        multiline
        onChange={(value) => {
          set("description", value);
        }}
        error={shown("description")}
      />
      <CheckList
        tokens={disciplineTokens}
        selected={draft.disciplines}
        onToggle={(id) => {
          set("disciplines", toggled(draft.disciplines, id));
        }}
        testId="project-create-discipline"
        label={strings.portfolio.createDisciplinesLabel}
      />
      <FieldError id="project-create-disciplines-error" message={shown("disciplines")} />
      <CheckList
        tokens={stageTokens}
        selected={draft.stages}
        onToggle={(id) => {
          set("stages", toggled(draft.stages, id));
        }}
        testId="project-create-stage"
        label={strings.portfolio.createStagesLabel}
      />
      <FieldError id="project-create-stages-error" message={shown("stages")} />

      <FormSection>{copy.contractSection}</FormSection>
      <Field>
        <TextField
          id="contract-create-title"
          label={copy.contractTitleLabel}
          value={draft.contractTitle}
          onChange={(value) => {
            set("contractTitle", value);
          }}
          error={shown("contractTitle")}
        />
        <FieldHelper>{copy.contractReferenceNote}</FieldHelper>
      </Field>
      <TextField
        id="contract-create-scope"
        label={copy.contractScopeLabel}
        value={draft.contractScope}
        multiline
        onChange={(value) => {
          set("contractScope", value);
        }}
        error={shown("contractScope")}
      />
      {contractScopeTokens.length === 0 ? (
        <FieldHelper data-testid="contract-disciplines-empty">
          {copy.contractDisciplinesEmpty}
        </FieldHelper>
      ) : (
        <CheckList
          tokens={contractScopeTokens}
          selected={draft.contractDisciplines}
          onToggle={(id) => {
            set("contractDisciplines", toggled(draft.contractDisciplines, id));
          }}
          testId="contract-create-discipline"
          label={copy.contractDisciplinesLabel}
        />
      )}
      <FieldError id="contract-create-disciplines-error" message={shown("contractDisciplines")} />
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
        <TextField
          id="contract-create-start"
          type="date"
          label={copy.contractStartLabel}
          value={draft.contractStart}
          onChange={(value) => {
            set("contractStart", value);
          }}
          error={shown("contractStart")}
        />
        <TextField
          id="contract-create-end"
          type="date"
          label={copy.contractEndLabel}
          value={draft.contractEnd}
          onChange={(value) => {
            set("contractEnd", value);
          }}
          error={shown("contractEnd")}
        />
        <TextField
          id="contract-create-value"
          label={copy.contractValueLabel}
          value={draft.contractValue}
          onChange={(value) => {
            set("contractValue", value);
          }}
          error={shown("contractValue")}
        />
        <Field>
          <SelectField
            id="contract-create-currency"
            label={copy.contractCurrencyLabel}
            value={draft.contractCurrency}
            invalid={shown("contractCurrency") !== null}
            onChange={(value) => {
              set("contractCurrency", value);
            }}
          >
            <option value="">{copy.currencyNone}</option>
            {CURRENCIES.map((one) => (
              <option key={one.code} value={one.code}>
                {one.name} ({one.code})
              </option>
            ))}
          </SelectField>
          <FieldError id="contract-create-currency-error" message={shown("contractCurrency")} />
        </Field>
      </div>

      <FormSection>{copy.contractorSection}</FormSection>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-sm tracking-[0.04em] text-muted-foreground">
          {copy.contractorModeQuestion}
        </legend>
        <RadioGroup
          value={draft.contractorMode}
          onValueChange={(value) => {
            set("contractorMode", value === "new" ? "new" : "existing");
          }}
        >
          <Label className="min-h-11 cursor-pointer text-base tracking-normal text-foreground">
            <RadioGroupItem value="existing" data-testid="contractor-mode-existing" />
            {copy.contractorModeExisting}
          </Label>
          <Label
            className={`min-h-11 text-base tracking-normal ${mayRegisterContractor ? "cursor-pointer text-foreground" : "text-muted-foreground"}`}
          >
            <RadioGroupItem
              value="new"
              data-testid="contractor-mode-new"
              disabled={!mayRegisterContractor}
            />
            {copy.contractorModeNew}
          </Label>
        </RadioGroup>
        {!mayRegisterContractor && (
          <FieldHelper data-testid="contractor-mode-new-reason">
            {copy.contractorModeNewNotYours}
          </FieldHelper>
        )}
      </fieldset>

      {draft.contractorMode === "existing" && (
        <Field>
          <SelectField
            id="contractor-existing"
            label={copy.contractorPickLabel}
            value={draft.contractorPartyId}
            invalid={shown("contractorPartyId") !== null}
            onChange={(value) => {
              set("contractorPartyId", value);
              // Accounts belong to ONE contractor: a new choice drops the old pick.
              set("portalPersonId", "");
              set("portalPersonName", "");
            }}
          >
            <option value="">Choose a contractor</option>
            {contractorOptions.map((one) => (
              <option key={one.id} value={one.id}>
                {one.name}
              </option>
            ))}
          </SelectField>
          {parties.kind === "ready" && contractorOptions.length === 0 && (
            <FieldHelper data-testid="contractor-existing-empty">
              {copy.contractorPickEmpty}
            </FieldHelper>
          )}
          {parties.kind === "failed" && (
            <p className="text-sm text-danger" role="alert">
              {strings.common.failed}
            </p>
          )}
          <FieldError id="contractor-existing-error" message={shown("contractorPartyId")} />
          {draft.contractorPartyId !== "" && (
            <PartyAccountPicker
              key={draft.contractorPartyId}
              partyId={draft.contractorPartyId}
              partyName={contractorName}
              value={draft.portalPersonId}
              onChange={(personId, name) => {
                set("portalPersonId", personId);
                set("portalPersonName", name);
              }}
            />
          )}
        </Field>
      )}

      {draft.contractorMode === "new" && (
        <p className="text-sm text-muted-foreground" data-testid="new-contractor-accounts-note">
          {strings.portalAccounts.newContractorNote}
        </p>
      )}
      {draft.contractorMode === "new" && (
        <div
          className="flex flex-col gap-3 border-s-[3px] border-rule-strong ps-4"
          data-testid="new-contractor-form"
        >
          {!typeAvailable && taxonomies.kind === "ready" && (
            <p className="text-sm text-danger" role="note" data-testid="new-contractor-no-type">
              {copy.newContractorNoType}
            </p>
          )}
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
            <TextField
              id="new-contractor-name"
              label={copy.newContractorName}
              value={draft.newName}
              onChange={(value) => {
                set("newName", value);
              }}
              error={shown("newName")}
            />
            {typeAvailable && (
              <Field>
                <SelectField
                  id="new-contractor-type"
                  label={copy.newContractorType}
                  invalid={shown("newTypeId") !== null}
                  value={typeId}
                  onChange={(value) => {
                    set("newTypeId", value);
                  }}
                >
                  <option value="">Choose a type</option>
                  {contractorTypes.map((one) => (
                    <option key={one.id} value={one.id}>
                      {one.label}
                    </option>
                  ))}
                </SelectField>
                <FieldError id="new-contractor-type-error" message={shown("newTypeId")} />
              </Field>
            )}
            <TextField
              id="new-contractor-registration"
              label={copy.newContractorRegistration}
              value={draft.newRegistration}
              onChange={(value) => {
                set("newRegistration", value);
              }}
              error={shown("newRegistration")}
            />
            <Field>
              <SelectField
                id="new-contractor-country"
                label={copy.newContractorCountry}
                value={draft.newCountry}
                invalid={shown("newCountry") !== null}
                onChange={(value) => {
                  set("newCountry", value);
                }}
              >
                <option value="">{copy.countryNone}</option>
                {COUNTRIES.map((one) => (
                  <option key={one.code} value={one.code}>
                    {one.name}
                  </option>
                ))}
              </SelectField>
              <FieldError id="new-contractor-country-error" message={shown("newCountry")} />
            </Field>
          </div>
          <TextField
            id="new-contractor-contact"
            label={copy.newContractorContact}
            value={draft.newContact}
            multiline
            onChange={(value) => {
              set("newContact", value);
            }}
            error={shown("newContact")}
          />
        </div>
      )}

      <FormSection>{copy.documentsSection}</FormSection>
      <Field>
        <FieldHelper>{copy.attachHelper}</FieldHelper>
        <FilePicker
          files={files}
          onChange={setFiles}
          label={copy.attachLabel}
          testId="project-create-files"
          emptyText={copy.attachNone}
          disabled={busy}
        />
      </Field>

      <Refusal message={refusal} />

      <SubmitRow
        reason={
          newModeBlocked && (
            <span data-testid="project-create-blocked">
              {mayRegisterContractor ? copy.newContractorNoType : copy.contractorModeNewNotYours}
            </span>
          )
        }
      >
        <Button
          variant="ghost"
          data-testid="project-create-clear"
          onClick={() => {
            setDraft(EMPTY_DRAFT);
            setFiles([]);
            setTried(false);
            setRefusal(null);
            setServerErrors({});
          }}
        >
          {copy.clearDraft}
        </Button>
        <Button variant="outline" onClick={onClose}>
          {strings.common.cancel}
        </Button>
        <Button
          data-testid="project-create-submit"
          disabled={busy || newModeBlocked}
          aria-disabled={busy || newModeBlocked}
          aria-busy={busy}
          onClick={() => {
            void submit();
          }}
        >
          {busy && <LoaderCircle className="animate-spin" aria-hidden="true" />}
          {busy ? copy.submitBusy : copy.submit}
        </Button>
      </SubmitRow>
    </ActionDialog>
  );
}

/* ---------------------------------------------------------------------- */
/* The person and role picker, fed by what the API already shows.         */
/* ---------------------------------------------------------------------- */

export interface Candidate {
  readonly id: string;
  readonly label: string;
}

interface Candidates {
  readonly people: readonly Candidate[];
  readonly roles: readonly Candidate[];
}

/** How many visible projects the picker reads for colleagues. Bounded on purpose. */
const CANDIDATE_PROJECT_LIMIT = 25;

/**
 * There is no people or roles directory endpoint yet, so the picker offers the operator
 * people and roles the API already shows this caller: the working teams of the projects
 * it can see, and the named registrant and approver of this one. Nothing is invented.
 */
async function readCandidates(
  token: string,
  seed: ProjectDetail | null,
  signal: AbortSignal,
): Promise<Candidates> {
  const people = new Map<string, string>();
  const roles = new Map<string, string>();
  const take = (detail: ProjectDetail): void => {
    for (const member of detail.team) {
      people.set(member.personId, member.displayName);
      roles.set(member.roleId, member.roleName);
    }
    if (detail.registeredBy && detail.registeredByDisplayName) {
      people.set(detail.registeredBy, detail.registeredByDisplayName);
    }
    if (detail.approvedBy && detail.approvedByDisplayName) {
      people.set(detail.approvedBy, detail.approvedByDisplayName);
    }
  };
  if (seed !== null) {
    take(seed);
  }
  const listed = await listProjects(token, { signal });
  const others = listed.items
    .filter((one) => one.id !== seed?.id)
    .slice(0, CANDIDATE_PROJECT_LIMIT);
  const details = await Promise.allSettled(others.map((one) => getProject(token, one.id, signal)));
  for (const result of details) {
    if (result.status === "fulfilled") {
      take(result.value);
    }
  }
  const sorted = (map: Map<string, string>): Candidate[] =>
    [...map.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  return { people: sorted(people), roles: sorted(roles) };
}

/**
 * The operator people and roles this caller can already see (the teams of its visible
 * projects), for a picker that has no directory to read: the credential holder and the
 * staffing rule's role (wave b12 frontend item 9).
 */
export async function readColleagues(
  token: string,
  signal: AbortSignal,
): Promise<{ readonly people: readonly Candidate[]; readonly roles: readonly Candidate[] }> {
  return readCandidates(token, null, signal);
}

/** One person the dialog will add, in one role, named for the screen. */
export interface PendingMember extends AssignPayload {
  readonly personLabel: string;
  readonly roleLabel: string;
  /** Wave INST ruling 5: the one discipline the role is given for; empty for every one. */
  readonly disciplineId?: string;
  readonly disciplineLabel?: string;
}

/** The discipline a member or pick is narrowed to, or null for every discipline. */
function disciplineOf(member: { readonly disciplineId?: string | null }): string | null {
  return member.disciplineId === undefined ||
    member.disciplineId === null ||
    member.disciplineId === ""
    ? null
    : member.disciplineId;
}

const NO_PICK: PendingMember = { personId: "", roleId: "", personLabel: "", roleLabel: "" };

/**
 * The team, in one place (wave W1.5, Mahmoud's walk finding 5): who is on it NOW (who
 * assigned whom, in what role, when), and the people this sitting will ADD. Adding never
 * replaces; removing a current member is its own explicit, confirmed step, offered only
 * where `onRemove` is given. A pick already on the team, or already listed, says so.
 *
 * `pending` holds the people added to the list; a complete pick still in the two selects
 * counts too (`withPick`), so one person in one role needs no extra click.
 */
function TeamEditor({
  token,
  seed,
  current,
  pending,
  onPendingChange,
  pick,
  onPickChange,
  idPrefix,
  onRemove,
  busy = false,
  requiredRole = null,
  disciplines = null,
}: {
  readonly token: string;
  readonly seed: ProjectDetail | null;
  readonly current: readonly TeamMember[];
  readonly pending: readonly PendingMember[];
  readonly onPendingChange: (next: PendingMember[]) => void;
  readonly pick: PendingMember;
  readonly onPickChange: (next: PendingMember) => void;
  readonly idPrefix: string;
  readonly onRemove?: (member: TeamMember) => Promise<void>;
  readonly busy?: boolean;
  /** A role the sitting must be able to offer even when no team shows it yet. */
  readonly requiredRole?: { readonly id: string; readonly label: string } | null;
  /**
   * Wave INST ruling 5: offered, the pick may narrow the role to one discipline (left
   * empty, the person holds it for every discipline). The approval's team takes none.
   */
  readonly disciplines?: readonly TaxonomyToken[] | null;
}): React.JSX.Element {
  const candidates = useQuery((signal) => readCandidates(token, seed, signal), [token, seed?.id]);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const pickProblem = pickConflict(pick, current, pending);

  const currentBlock = (
    <section className="flex flex-col gap-2" data-testid={`${idPrefix}-current`}>
      <FormSection>{copy.teamCurrentHeading}</FormSection>
      {current.length === 0 ? (
        <FieldHelper data-testid={`${idPrefix}-current-empty`}>{copy.teamNone}</FieldHelper>
      ) : (
        <ul className="flex flex-col gap-1">
          {current.map((member) => {
            const key = member.id ?? `${member.personId} ${member.roleId}`;
            // undefined: not read yet; null: no person did it (set up with the system).
            const by =
              member.assignedByDisplayName === undefined
                ? null
                : member.assignedByDisplayName === null
                  ? copy.teamAssignedBySeed
                  : copy.teamAssignedBy(
                      member.assignedByDisplayName,
                      member.assignedAt ? day(member.assignedAt.slice(0, 10)) : "",
                    );
            return (
              <li
                key={key}
                className="flex flex-col gap-2 rounded-md border border-rule px-3 py-2"
                data-testid={`${idPrefix}-current-member`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-base text-foreground">
                    {member.displayName}, {member.roleName}
                    {disciplineOf(member) !== null && (
                      <span data-testid={`${idPrefix}-current-discipline`}>
                        {" "}
                        ·{" "}
                        {copy.assignDisciplineOnly(
                          disciplines?.find((one) => one.id === member.disciplineId)?.label ??
                            copy.assignDisciplineUnnamed,
                        )}
                      </span>
                    )}
                    <span className="block text-sm text-muted-foreground">
                      {member.scope === "CONTRACT"
                        ? copy.teamScopeAContract
                        : copy.teamScopeProject}
                      {by !== null && ` · ${copy.teamColumnBy.toLowerCase()} ${by}`}
                    </span>
                  </span>
                  {onRemove !== undefined && member.id !== undefined && confirming !== key && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy || removing}
                      data-testid={`${idPrefix}-remove`}
                      aria-label={copy.teamRemove(member.displayName)}
                      onClick={() => {
                        setConfirming(key);
                      }}
                    >
                      {copy.teamRemove(member.displayName)}
                    </Button>
                  )}
                </div>
                {onRemove !== undefined && confirming === key && (
                  <div
                    className="flex flex-col gap-2 rounded-md border border-danger-edge bg-danger-wash p-2"
                    role="alert"
                    data-testid={`${idPrefix}-remove-confirm`}
                  >
                    <p className="text-sm text-foreground">
                      {copy.teamRemoveConfirm(member.displayName, member.roleName)}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={removing}
                        data-testid={`${idPrefix}-remove-yes`}
                        onClick={() => {
                          setRemoving(true);
                          void onRemove(member).finally(() => {
                            setRemoving(false);
                            setConfirming(null);
                          });
                        }}
                      >
                        {copy.teamRemoveYes}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={removing}
                        onClick={() => {
                          setConfirming(null);
                        }}
                      >
                        {copy.teamRemoveNo}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );

  if (candidates.kind === "loading") {
    return (
      <>
        {currentBlock}
        <p className="text-sm text-muted-foreground" role="status">
          {strings.common.loading}
        </p>
      </>
    );
  }
  if (candidates.kind !== "ready") {
    return (
      <>
        {currentBlock}
        <p className="text-sm text-danger" role="alert">
          {strings.common.failed}
        </p>
      </>
    );
  }
  const { people } = candidates.data;
  const roles =
    requiredRole !== null && !candidates.data.roles.some((one) => one.id === requiredRole.id)
      ? [requiredRole, ...candidates.data.roles]
      : candidates.data.roles;
  const labelOf = (list: readonly Candidate[], id: string): string =>
    list.find((one) => one.id === id)?.label ?? "";
  return (
    <>
      {currentBlock}
      <FormSection>{copy.teamAddHeading}</FormSection>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2" data-testid={`${idPrefix}-picker`}>
        <Field>
          <SelectField
            id={`${idPrefix}-person`}
            label={copy.assignPersonLabel}
            value={pick.personId}
            onChange={(next) => {
              onPickChange({ ...pick, personId: next, personLabel: labelOf(people, next) });
            }}
          >
            <option value="">Choose a person</option>
            {people.map((one) => (
              <option key={one.id} value={one.id}>
                {one.label}
              </option>
            ))}
          </SelectField>
          {people.length === 0 && <FieldHelper>{copy.assignNoPeople}</FieldHelper>}
        </Field>
        <Field>
          <SelectField
            id={`${idPrefix}-role`}
            label={copy.assignRoleLabel}
            value={pick.roleId}
            onChange={(next) => {
              onPickChange({ ...pick, roleId: next, roleLabel: labelOf(roles, next) });
            }}
          >
            <option value="">Choose a role</option>
            {roles.map((one) => (
              <option key={one.id} value={one.id}>
                {one.label}
              </option>
            ))}
          </SelectField>
          {roles.length === 0 && <FieldHelper>{copy.assignNoRoles}</FieldHelper>}
        </Field>
        {disciplines !== null && (
          <Field className="sm:col-span-2">
            <SelectField
              id={`${idPrefix}-discipline`}
              label={copy.assignDisciplineLabel}
              value={pick.disciplineId ?? ""}
              onChange={(next) => {
                onPickChange({
                  ...pick,
                  disciplineId: next,
                  disciplineLabel: disciplines.find((one) => one.id === next)?.label ?? "",
                });
              }}
            >
              <option value="">{copy.assignDisciplineEvery}</option>
              {disciplines
                .filter((one) => one.id !== undefined)
                .map((one) => (
                  <option key={one.id} value={one.id}>
                    {one.label}
                  </option>
                ))}
            </SelectField>
            <FieldHelper>{copy.assignDisciplineHelper}</FieldHelper>
          </Field>
        )}
        <FieldHelper className="sm:col-span-2">{copy.assignCandidatesNote}</FieldHelper>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          data-testid={`${idPrefix}-add`}
          disabled={busy || pick.personId === "" || pick.roleId === "" || pickProblem !== null}
          onClick={() => {
            onPendingChange([...pending, pick]);
            onPickChange(NO_PICK);
          }}
        >
          {copy.teamAddButton}
        </Button>
        {pickProblem !== null && (
          <span className="text-sm text-danger" data-testid={`${idPrefix}-pick-problem`}>
            {pickProblem}
          </span>
        )}
      </div>
      {pending.length > 0 && (
        <section className="flex flex-col gap-2" data-testid={`${idPrefix}-pending-list`}>
          <FormSection>{copy.teamPendingHeading}</FormSection>
          <ul className="flex flex-col gap-1">
            {pending.map((member, index) => (
              <li
                key={`${member.personId} ${member.roleId} ${member.disciplineId ?? ""}`}
                className="flex min-h-11 items-center justify-between gap-3 rounded-md border border-rule px-3"
                data-testid={`${idPrefix}-pending`}
              >
                <span className="text-base text-foreground">
                  {member.personLabel}, {member.roleLabel}
                  {disciplineOf(member) !== null &&
                    ` · ${copy.assignDisciplineOnly(member.disciplineLabel ?? "")}`}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  aria-label={copy.teamPendingRemove(member.personLabel)}
                  data-testid={`${idPrefix}-pending-remove`}
                  onClick={() => {
                    onPendingChange(pending.filter((_, at) => at !== index));
                  }}
                >
                  <X aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <FieldHelper>{copy.teamAddsNote}</FieldHelper>
    </>
  );
}

/** Why a pick cannot be added: already on the team, or already on the list. */
function pickConflict(
  pick: PendingMember,
  current: readonly TeamMember[],
  pending: readonly PendingMember[],
): string | null {
  if (pick.personId === "" || pick.roleId === "") {
    return null;
  }
  // Wave INST ruling 5: one person may hold one role for two disciplines; the same
  // person, role and discipline (or both for every discipline) is the same assignment.
  const onTeam = current.find(
    (one) =>
      one.personId === pick.personId &&
      one.roleId === pick.roleId &&
      disciplineOf(one) === disciplineOf(pick),
  );
  if (onTeam !== undefined) {
    return copy.teamAlreadyOn(onTeam.displayName, onTeam.roleName);
  }
  if (
    pending.some(
      (one) =>
        one.personId === pick.personId &&
        one.roleId === pick.roleId &&
        disciplineOf(one) === disciplineOf(pick),
    )
  ) {
    return copy.teamAlreadyListed(pick.personLabel, pick.roleLabel);
  }
  return null;
}

/** The list the sitting sends: the added people, and a complete pick not yet added. */
function withPick(
  pending: readonly PendingMember[],
  pick: PendingMember,
  current: readonly TeamMember[],
): PendingMember[] {
  const complete = pick.personId !== "" && pick.roleId !== "";
  return complete && pickConflict(pick, current, pending) === null
    ? [...pending, pick]
    : [...pending];
}

/* ---------------------------------------------------------------------- */
/* The approval overlay.                                                  */
/* ---------------------------------------------------------------------- */

export interface VendorGap {
  readonly party: string;
  readonly discipline: string;
}

/**
 * The server names the gaps inside its refusal ("... vendor for: Builder Ltd (Civil),
 * Builder Ltd (Piping). ..."). Read the names; never show the sentence's API wording.
 * Kept as the fallback for a server that refuses although the outlook said otherwise.
 */
export function parseVendorGaps(detail: string): VendorGap[] {
  const listed = /for: (.+?)\.(?: |$)/.exec(detail)?.[1] ?? "";
  const gaps: VendorGap[] = [];
  for (const match of listed.matchAll(/\s*([^,]+?) \(([^)]+)\)/g)) {
    const party = match[1]?.trim() ?? "";
    const discipline = match[2]?.trim() ?? "";
    if (party !== "" && discipline !== "") {
      gaps.push({ party, discipline });
    }
  }
  return gaps;
}

/** The server's own sentence, or the transport's when the refusal carried none. */
function problemText(error: ApiError): string {
  return refusalText(error);
}

function approvalRefusal(error: ApiError): string {
  if (error.kind === "absence") {
    return copy.approveAbsent;
  }
  switch (error.problem?.rule) {
    case "project.self_approval":
      return copy.selfApproval;
    case "vendor.approval_not_permitted":
      return copy.vendorNotPermitted;
    case "vendor.self_approval":
      return copy.vendorSelfApproval;
    case "project.not_pending_approval":
      return copy.notPending;
    default:
      return problemText(error);
  }
}

/** Who holds vendor approval, in plain words: roles only, never people. */
export function vendorHolderNames(outlook: ApprovalOutlook): string[] {
  return outlook.vendorApprovers.map((one) =>
    one.roleLabel === null
      ? copy.vendorHolderEveryone
      : one.byGrant
        ? copy.vendorHolderByGrant(one.roleLabel)
        : one.roleLabel,
  );
}

export function ApprovalDialog({
  token,
  project,
  onClose,
  onApproved,
}: {
  readonly token: string;
  readonly project: ProjectDetail;
  readonly onClose: () => void;
  readonly onApproved: (result: ProjectApproved) => void;
}): React.JSX.Element {
  // One project, one main contract, one main contractor (wave W0): the approval names
  // the MAIN contractor, never a sub-contractor or a retired second contract.
  const contracts = useQuery(
    (signal) => listContracts(token, { projectId: project.id, mainOnly: true, signal }),
    [token, project.id],
  );
  // Wave W1.5, finding 4: what the act will meet, and whether THIS account can take it,
  // is read BEFORE the attempt. No path is offered that the account cannot complete.
  const outlook = useQuery(
    (signal) => getApprovalOutlook(token, project.id, signal),
    [token, project.id],
  );
  const [lateGaps, setLateGaps] = useState<VendorGap[] | null>(null);
  const [approveVendor, setApproveVendor] = useState(false);
  const [assignOn, setAssignOn] = useState(false);
  const [pending, setPending] = useState<PendingMember[]>([]);
  const [pick, setPick] = useState<PendingMember>(NO_PICK);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const contractors =
    contracts.kind === "ready"
      ? [...new Set(contracts.data.items.map((one) => one.contractorPartyName))]
      : [];
  const view = outlook.kind === "ready" ? outlook.data : null;
  const gaps: VendorGap[] =
    view !== null && view.vendorGaps.length > 0
      ? view.vendorGaps.map((gap) => ({ party: gap.partyName, discipline: gap.disciplineLabel }))
      : (lateGaps ?? []);
  const vendorBlocked =
    view !== null &&
    view.vendorGaps.length > 0 &&
    (!view.mayApproveVendor || view.vendorSelfRegistered);
  const vendorOffered = gaps.length > 0 && !vendorBlocked;
  const team = withPick(pending, pick, project.team);
  // Mahmoud's ruling (2026-10-11): "the approval chain cannot complete until there is
  // someone in the team assigned to that contract." When the team has no QA/QC engineer
  // the assignment is not optional: it is stated up front and the approval waits for it.
  const engineerRequired = view?.qaqcEngineerRequired === true;
  const assigning = assignOn || engineerRequired;
  const engineerNamed =
    view?.qaqcRole !== null &&
    view?.qaqcRole !== undefined &&
    team.some((one) => one.roleId === view.qaqcRole?.id);
  const engineerMissing = engineerRequired && !engineerNamed;
  const assignIncomplete = assigning && team.length === 0;
  const vendorUnconfirmed = vendorOffered && !approveVendor;
  const outlookUnread = outlook.kind !== "ready";
  const blocked =
    busy ||
    assignIncomplete ||
    engineerMissing ||
    vendorUnconfirmed ||
    vendorBlocked ||
    outlookUnread;

  const submit = async (): Promise<void> => {
    if (blocked) {
      return;
    }
    setBusy(true);
    setRefusal(null);
    try {
      const result = await approveProject(token, project.id, {
        approveVendor: vendorOffered && approveVendor,
        team: assigning ? team : [],
      });
      onApproved(result);
    } catch (error: unknown) {
      if (error instanceof ApiError && error.problem?.rule === "project.vendor_approval_required") {
        // The outlook missed a gap (the list changed meanwhile): show the step now.
        setLateGaps(parseVendorGaps(error.problem.detail));
      } else if (error instanceof ApiError) {
        setRefusal(approvalRefusal(error));
      } else {
        setRefusal(strings.common.failed);
      }
    } finally {
      setBusy(false);
    }
  };

  const party = gaps[0]?.party ?? contractors[0] ?? "";
  return (
    <ActionDialog open wide title={copy.approveHeading} onClose={onClose}>
      <p className="text-base text-muted-foreground">{copy.approveIntro}</p>
      <div className="rounded-md border border-rule bg-card p-4">
        <Facts
          entries={[
            [strings.common.humanId, project.humanId],
            [strings.portfolio.columnProject, project.name],
            [strings.portfolio.createNumberLabel, project.projectNumber],
            [
              strings.portfolio.columnDates,
              `${day(project.startDate)} to ${day(project.endDate ?? project.completionDate)}`,
            ],
            [copy.registeredBy, project.registeredByDisplayName ?? copy.someone],
            [
              copy.approveContractorLabel,
              contracts.kind === "loading"
                ? strings.common.loading
                : contractors.length > 0
                  ? contractors.join(", ")
                  : copy.notYet,
            ],
            [strings.portfolio.columnStatus, <ProjectStatusChip key="s" status={project.status} />],
          ]}
        />
      </div>

      {outlook.kind === "loading" && (
        <FieldHelper data-testid="vendor-state-pending" role="status">
          {copy.outlookLoading}
        </FieldHelper>
      )}
      {(outlook.kind === "failed" || outlook.kind === "absence") && (
        <Refusal
          testId="outlook-failed"
          message={outlook.kind === "absence" ? copy.approveAbsent : copy.outlookFailed}
        />
      )}
      {view !== null && gaps.length === 0 && (
        <FieldHelper data-testid="vendor-state-clear">{copy.vendorClear}</FieldHelper>
      )}

      {vendorOffered && (
        <section
          className="flex flex-col gap-2 rounded-md border border-gold-soft border-s-[3px] border-s-gold bg-gold-wash p-4"
          data-testid="vendor-step"
          aria-live="polite"
        >
          <p className="text-xs tracking-[0.12em] text-gold uppercase">{copy.vendorStepHeading}</p>
          <p>{party === "" ? copy.vendorStepIntro : copy.vendorStepIntroFor(party)}</p>
          <ul className="list-disc ps-5" data-testid="vendor-gaps">
            {gaps.map((gap) => (
              <li key={`${gap.party} ${gap.discipline}`}>
                {gap.party}: {gap.discipline}
              </li>
            ))}
          </ul>
          <Label className="min-h-11 cursor-pointer text-base tracking-normal text-foreground">
            <Checkbox
              data-testid="vendor-step-confirm"
              checked={approveVendor}
              onCheckedChange={() => {
                setApproveVendor((value) => !value);
              }}
            />
            {copy.vendorStepConfirm}
          </Label>
          <FieldHelper>{copy.vendorStepHelper}</FieldHelper>
        </section>
      )}

      {vendorBlocked && (
        <section
          className="flex flex-col gap-2 rounded-md border border-danger-edge border-s-[3px] border-s-danger bg-danger-wash p-4"
          data-testid="vendor-blocked"
          aria-live="polite"
        >
          <p className="text-xs tracking-[0.12em] text-danger uppercase">
            {copy.vendorBlockedHeading}
          </p>
          <p>{copy.vendorStepIntroFor(party)}</p>
          <ul className="list-disc ps-5" data-testid="vendor-gaps">
            {gaps.map((gap) => (
              <li key={`${gap.party} ${gap.discipline}`}>
                {gap.party}: {gap.discipline}
              </li>
            ))}
          </ul>
          <p data-testid="vendor-blocked-why">
            {view.vendorSelfRegistered ? copy.vendorBlockedSelf : copy.vendorBlockedNoVerb}{" "}
            {copy.vendorBlockedWaits}
          </p>
          <p data-testid="vendor-holders">{copy.vendorHolders(vendorHolderNames(view))}</p>
          <p data-testid="vendor-able">
            {view.ableToCompleteRoles.length > 0
              ? copy.ableToComplete(view.ableToCompleteRoles)
              : copy.ableToCompleteNone}
          </p>
        </section>
      )}

      {!vendorBlocked && (
        <>
          {engineerRequired ? (
            <section
              className="flex flex-col gap-2 rounded-md border border-gold-soft border-s-[3px] border-s-gold bg-gold-wash p-4"
              data-testid="approve-engineer-required"
              aria-live="polite"
            >
              <p className="text-xs tracking-[0.12em] text-gold uppercase">
                {copy.engineerRequiredHeading}
              </p>
              <p>{copy.engineerRequiredBody(view.mainContractHumanId)}</p>
            </section>
          ) : (
            <Field>
              <Label className="min-h-11 cursor-pointer text-base tracking-normal text-foreground">
                <Checkbox
                  data-testid="approve-assign-toggle"
                  checked={assignOn}
                  onCheckedChange={() => {
                    setAssignOn((value) => !value);
                  }}
                />
                {copy.assignNowLabel}
              </Label>
              <FieldHelper>{copy.assignNowHelper}</FieldHelper>
            </Field>
          )}
          {assigning && (
            <TeamEditor
              requiredRole={engineerRequired ? (view.qaqcRole ?? null) : null}
              token={token}
              seed={project}
              current={project.team}
              pending={pending}
              onPendingChange={setPending}
              pick={pick}
              onPickChange={setPick}
              idPrefix="approve-assign"
              busy={busy}
            />
          )}
        </>
      )}

      <Refusal message={refusal} />

      <SubmitRow
        reason={
          <>
            {vendorBlocked && (
              <span className="block" data-testid="approve-blocked-capability">
                {copy.approveBlockedCapability}
              </span>
            )}
            {vendorUnconfirmed && (
              <span className="block" data-testid="approve-blocked-vendor">
                {copy.vendorStepRequired}
              </span>
            )}
            {assignIncomplete && !engineerRequired && (
              <span className="block" data-testid="approve-blocked-assign">
                {copy.assignStepRequired}
              </span>
            )}
            {engineerMissing && (
              <span className="block" data-testid="approve-blocked-engineer">
                {copy.engineerRequiredReason(view.qaqcRole?.label ?? null)}
              </span>
            )}
          </>
        }
      >
        <Button variant="outline" onClick={onClose}>
          {strings.common.cancel}
        </Button>
        <Button
          data-testid="approve-submit"
          disabled={blocked}
          aria-disabled={blocked}
          aria-busy={busy}
          onClick={() => {
            void submit();
          }}
        >
          {busy && <LoaderCircle className="animate-spin" aria-hidden="true" />}
          {busy
            ? copy.approveBusy
            : vendorOffered
              ? copy.approveWithVendorSubmit
              : copy.approveSubmit}
        </Button>
      </SubmitRow>
    </ActionDialog>
  );
}

/* ---------------------------------------------------------------------- */
/* The assignment overlay.                                                */
/* ---------------------------------------------------------------------- */

export interface AssignOutcome {
  readonly personLabel: string;
  readonly itpsRequested: boolean;
}

export function AssignDialog({
  token,
  target,
  seed,
  teamProjectId,
  onClose,
  onAssigned,
}: {
  readonly token: string;
  readonly target: { readonly projectId: string } | { readonly contractId: string };
  readonly seed: ProjectDetail | null;
  /** Whose team the dialog shows: the project, or the project a contract is under. */
  readonly teamProjectId?: string | null;
  readonly onClose: () => void;
  readonly onAssigned: (outcome: AssignOutcome) => void;
}): React.JSX.Element {
  const [pending, setPending] = useState<PendingMember[]>([]);
  const [pick, setPick] = useState<PendingMember>(NO_PICK);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [stamp, setStamp] = useState(0);
  const [added, setAdded] = useState<{ names: string[]; itps: boolean }>({
    names: [],
    itps: false,
  });
  // Wave INST ruling 5: the tenant's disciplines, for an assignment narrowed to one.
  const taxonomies = useQuery((signal) => getTaxonomies(token, signal), [token]);
  const disciplines =
    taxonomies.kind === "ready"
      ? taxonomies.data.disciplines.filter((one) => one.id !== undefined)
      : [];

  // The CURRENT team, read fresh (wave W1.5, finding 5b): nobody assigns blind.
  const teamProject =
    "projectId" in target ? target.projectId : (teamProjectId ?? seed?.id ?? null);
  const fresh = useQuery(
    (signal) =>
      teamProject === null ? Promise.resolve(null) : getProject(token, teamProject, signal),
    [token, teamProject, stamp],
  );
  const detail = fresh.kind === "ready" && fresh.data !== null ? fresh.data : seed;
  const current = (detail?.team ?? []).filter((member) =>
    "contractId" in target
      ? member.scope !== "CONTRACT" || member.scopeContractId === target.contractId
      : true,
  );
  const team = withPick(pending, pick, current);
  const incomplete = team.length === 0;

  const remove = async (member: TeamMember): Promise<void> => {
    if (member.id === undefined) {
      return;
    }
    setRefusal(null);
    setNotice(null);
    try {
      await revokeRoleAssignment(token, member.id);
      setNotice(copy.teamRemoved(member.displayName));
      setStamp((one) => one + 1);
    } catch (error: unknown) {
      setRefusal(
        error instanceof ApiError && error.kind === "absence"
          ? copy.assignAbsent
          : refusalText(error),
      );
    }
  };

  const submit = async (): Promise<void> => {
    if (busy || incomplete) {
      return;
    }
    setBusy(true);
    setRefusal(null);
    setNotice(null);
    const failed: PendingMember[] = [];
    let lastError: unknown = null;
    const names = [...added.names];
    let itps = added.itps;
    for (const member of team) {
      try {
        const result = await createRoleAssignment(token, {
          personId: member.personId,
          roleId: member.roleId,
          disciplineId: disciplineOf(member),
          ...("projectId" in target
            ? { projectId: target.projectId }
            : { contractId: target.contractId }),
        });
        names.push(member.personLabel);
        itps = itps || result.itpsRequested;
      } catch (error: unknown) {
        failed.push(member);
        lastError = error;
      }
    }
    setBusy(false);
    setAdded({ names, itps });
    if (failed.length === 0) {
      onAssigned({ personLabel: names.join(", "), itpsRequested: itps });
      return;
    }
    setPending(failed);
    setPick(NO_PICK);
    setStamp((one) => one + 1);
    const reason =
      lastError instanceof ApiError && lastError.kind === "absence"
        ? copy.assignAbsent
        : refusalText(lastError);
    setRefusal(
      names.length > 0
        ? `${copy.assignPartlyFailed(failed.map((one) => one.personLabel))} ${reason}`
        : reason,
    );
  };

  return (
    <ActionDialog open wide title={copy.assignHeading} onClose={onClose}>
      <p className="text-base text-muted-foreground">{copy.assignIntro}</p>
      <TeamEditor
        token={token}
        seed={detail}
        current={current}
        pending={pending}
        onPendingChange={setPending}
        pick={pick}
        onPickChange={setPick}
        idPrefix="assign"
        onRemove={remove}
        busy={busy}
        disciplines={disciplines}
      />
      {notice !== null && (
        <p className="text-sm text-foreground" role="status" data-testid="assign-notice">
          {notice}
        </p>
      )}
      <Refusal message={refusal} />
      <SubmitRow
        reason={incomplete && <span data-testid="assign-blocked">{copy.assignRequired}</span>}
      >
        <Button variant="outline" onClick={onClose}>
          {strings.common.cancel}
        </Button>
        <Button
          data-testid="assign-submit"
          disabled={busy || incomplete}
          aria-disabled={busy || incomplete}
          aria-busy={busy}
          onClick={() => {
            void submit();
          }}
        >
          {busy && <LoaderCircle className="animate-spin" aria-hidden="true" />}
          {busy ? copy.assignBusy : copy.assignSubmitMany(team.length)}
        </Button>
      </SubmitRow>
    </ActionDialog>
  );
}

/* ---------------------------------------------------------------------- */
/* The provenance block: the project's tracked actions in plain words.    */
/* ---------------------------------------------------------------------- */

const OBJECT_NOUN: Record<string, string> = {
  PROJECT: "the project",
  CONTRACT: "the contract",
  PARTY: "the contractor",
  ROLE_ASSIGNMENT: "the project team",
};

const STATE_WORDS: Record<string, string> = {
  PENDING_APPROVAL: "waiting for approval",
  PENDING_ASSIGNMENT: "approved, waiting for personnel",
  REGISTERED: "registered",
};

function stateWord(state: string): string {
  return STATE_WORDS[state] ?? state.toLowerCase().replace(/_/g, " ");
}

export function historySentence(entry: {
  readonly objectType: string;
  readonly event: string | null;
  readonly fromState: string | null;
  readonly toState: string | null;
  readonly actorDisplayName: string | null;
}): string {
  const who = entry.actorDisplayName ?? copy.someone;
  const noun = OBJECT_NOUN[entry.objectType] ?? "a related record";
  switch (entry.event) {
    case "REGISTER":
      return `${who} registered ${noun}.`;
    case "APPROVE":
      return `${who} approved ${noun}.`;
    case "APPROVE_VENDOR":
      return `${who} approved the contractor as a vendor for the project's scope.`;
    case "ASSIGN":
      return `${who} assigned a person to ${noun}.`;
    case "REVOKE":
      return `${who} removed a person from ${noun}.`;
    case "ATTACH_DOCUMENT":
      return `${who} attached a document to ${noun}.`;
    case "REMOVE_DOCUMENT":
      return `${who} removed a document from ${noun}.`;
    case "PERSONNEL_ASSIGNED":
      return `${who} completed the personnel assignment; the project is registered.`;
    case "REQUEST_ITPS":
      return `${who} handed over to the contractor to author inspection and test plans.`;
    case "RETIRE":
      return `${entry.actorDisplayName ?? "Qualify"} retired ${noun}: it was registered in error as a second main contractor on this project.`;
    default:
      if (entry.fromState !== null && entry.toState !== null) {
        return `${who} moved ${noun} from ${stateWord(entry.fromState)} to ${stateWord(entry.toState)}.`;
      }
      return `${who} changed ${noun}.`;
  }
}

export function ProjectHistoryBlock({
  token,
  projectId,
  stamp,
}: {
  readonly token: string;
  readonly projectId: string;
  /** Bumped by the page after a write, so the history re-reads. */
  readonly stamp: number;
}): React.JSX.Element {
  const [retry, setRetry] = useState(0);
  const history = useQuery(
    (signal) => getProjectHistory(token, projectId, signal),
    [token, projectId, stamp, retry],
  );
  const rows = useMemo(
    () => (history.kind === "ready" ? [...history.data].sort((a, b) => a.seq - b.seq) : []),
    [history],
  );
  return (
    <section className="q-section" id="project-history" data-testid="project-history">
      <h3 className="q-section__heading">{copy.historyHeading}</h3>
      {history.kind === "loading" && (
        <p className="q-status" role="status">
          {strings.common.loading}
        </p>
      )}
      {(history.kind === "failed" || history.kind === "absence") && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{copy.historyFailed}</p>
          <button
            type="button"
            className="q-button"
            data-testid="project-history-retry"
            onClick={() => {
              setRetry((one) => one + 1);
            }}
          >
            {copy.retry}
          </button>
        </div>
      )}
      {history.kind === "ready" && rows.length === 0 && (
        <p className="q-note" data-testid="project-history-empty">
          {copy.historyEmpty}
        </p>
      )}
      {history.kind === "ready" && rows.length > 0 && (
        <ol className="q-history" data-testid="project-history-list">
          {rows.map((row) => (
            <li key={row.seq} className="q-history__row">
              <time className="q-register__mono" dateTime={row.at}>
                {row.at.slice(0, 16).replace("T", " ")}
              </time>{" "}
              <span>{historySentence(row)}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
